import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";

/**
 * Système de toast notifications minimal sans dépendance externe.
 *
 * Usage :
 *   const toast = useToast();
 *   toast.success("Site créé.");
 *   toast.error("Erreur de connexion.");
 *
 * Wrapper `<ToastProvider>` à mettre à la racine de l'app.
 */

export type ToastKind = "success" | "error" | "info" | "warning";

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  duration: number;
}

interface ToastApi {
  show: (kind: ToastKind, message: string, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  let nextId = 1;

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (kind: ToastKind, message: string, duration = 4000) => {
      const id = nextId++;
      setItems((prev) => [...prev, { id, kind, message, duration }]);
      window.setTimeout(() => remove(id), duration);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remove],
  );

  const api: ToastApi = {
    show,
    success: (m, d) => show("success", m, d),
    error: (m, d) => show("error", m, d ?? 6000),
    info: (m, d) => show("info", m, d),
    warning: (m, d) => show("warning", m, d),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[1000] flex flex-col items-end gap-2"
        aria-live="polite"
        aria-atomic="false"
      >
        <AnimatePresence initial={false}>
          {items.map((t) => (
            <ToastItem key={t.id} item={t} onClose={() => remove(t.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

const KIND_TONE: Record<ToastKind, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  error: "border-red-200 bg-red-50 text-red-800",
  info: "border-navy-200 bg-white text-navy-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
};

function ToastItem({
  item,
  onClose,
}: {
  item: ToastItem;
  onClose: () => void;
}) {
  // Reset le timer si l'utilisateur survole
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(onClose, item.duration);
    return () => clearTimeout(t);
  }, [paused, item.duration, onClose]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 40, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 350, damping: 26 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={onClose}
      className={`pointer-events-auto flex max-w-sm cursor-pointer items-start gap-2 rounded-xl border px-3 py-2.5 text-sm shadow-lg ${KIND_TONE[item.kind]}`}
      role="status"
    >
      <ToastIcon kind={item.kind} />
      <p className="flex-1 font-medium">{item.message}</p>
    </motion.div>
  );
}

function ToastIcon({ kind }: { kind: ToastKind }) {
  const stroke = "currentColor";
  const w = 18;
  const sw = 2.4;
  if (kind === "success") {
    return (
      <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    );
  }
  if (kind === "error") {
    return (
      <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    );
  }
  if (kind === "warning") {
    return (
      <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 shrink-0">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}
